/**
 * Phototherapy app settings — the single AppConfig row that decides which
 * calculator a hospital sees (`protocol`) and, for the AAP 2022 calculator,
 * every hospital-tunable number and text.
 *
 * AppConfig: appName from the variant (PHOTOTHERAPY | BILI2022), configName
 * settings, hospital <name> | apps. Every function here takes the variant rather
 * than assuming one, so the two registered apps can never read each other's row —
 * see `variants.ts`.
 *
 * TWO SEPARATE THINGS, deliberately kept apart:
 *
 *  1. `variant.defaultProtocol` — what a hospital with NO stored row gets. It is
 *     `israeliCurves` for צהבת ביילוד and `aap2022` for צהבת ביילוד AAP 2022.
 *     Any other protocol is in effect only because someone chose it on the admin
 *     page and saved it.
 *
 *  2. `DEFAULT_AAP2022_SETTINGS` (`data/aap2022Defaults.json`) — the starting
 *     values the admin page shows for the AAP fields when a hospital has not
 *     configured them yet. They are NOT the hospital's configuration: nothing is
 *     in effect until "שמור" writes an AppConfig row. From then on the admin page
 *     and the app both read the stored row.
 *
 * The defaults transcribe the departmental procedure in
 * `data/new-graphs-2026/phototherapy.docx` (AAP 2022 based) — thresholds,
 * neurotoxicity risk factors, follow-up table, lab panel, escalation steps.
 */
import { useCallback, useEffect, useState } from 'react';
import type { AxiosRequestConfig } from 'axios';
import API from '../../config/axiosConfig';
import aap2022DefaultsJson from './data/aap2022Defaults.json';
import type { PhototherapyVariant } from './variants';

export type PhototherapyProtocol = 'israeliCurves' | 'aap2022';

export interface FollowUpBand {
  /** Inclusive lower bound of (phototherapy threshold − bilirubin), mg/dL. */
  minDelta: number;
  /** Inclusive upper bound; null = open-ended. */
  maxDelta: number | null;
  recommendation: string;
}

/** One document on the "פרוטוקולים" screen. Mirrors `ConfigFileRef`. */
export interface ProtocolFile {
  /** The single line shown on the tile. */
  title: string;
  /** Blob path under apps/phototherapy/<hospital>/settings/. */
  path?: string;
  /** A document bundled with the app instead of stored — `BUILTIN_AAP_GUIDELINE`. */
  builtin?: string;
}

export interface Aap2022Settings {
  /** Below the phototherapy threshold by at most this → "approaching" warning. */
  subthresholdDeltaMgDl: number;
  /** Phototherapy may stop once TSB is this far below the current threshold (Israeli: 3, AAP: 2). */
  stopPhototherapyDeltaMgDl: number;
  /**
   * How far below the EXCHANGE threshold the escalation-of-care band starts
   * (procedure §10 / AAP 2022: 2 mg/dL). The escalation line is not in the
   * threshold dataset — `aapCalculation.ts` derives it from the exchange curve
   * and this number, so a hospital can widen or narrow the band.
   */
  escalationDeltaMgDl: number;
  /** A TcB within this distance of the phototherapy threshold must be confirmed by TSB. */
  tcbToTsbDeltaMgDl: number;
  /** A TcB at or above this absolute value must be confirmed by TSB. */
  tcbToTsbAbsoluteMgDl: number;
  /**
   * A level this close to (or above) the WITH-RISK phototherapy threshold for the
   * baby's gestational age and hours of life calls for a blood type + DAT sample,
   * whether or not the baby has risk factors of its own. null = never shown.
   */
  bloodTypeDatDeltaMgDl: number | null;
  /** TSB at or above this → recommend a hearing test (null = never shown). */
  hearingTestTsbMgDl: number | null;
  /**
   * Age from which a level within `prolongedJaundiceDeltaMgDl` of the threshold
   * counts as prolonged jaundice (null = the note is never shown). The
   * departmental procedure has no prolonged-jaundice rule, so it ships off.
   */
  prolongedJaundiceFromHours: number | null;
  prolongedJaundiceDeltaMgDl: number;
  /**
   * The neurotoxicity factors (procedure §7 table 1) decide WHICH CURVE is used —
   * this is the list behind the calculator's checkbox, and the only risk-factor
   * list the app has. A §1 "who is at risk of jaundice" factor must never be
   * added here: it would move a baby onto the lower curves for a reason the AAP
   * does not accept.
   */
  neurotoxicityRiskFactors: string[];
  /** Why the neurotoxicity list matters, shown under its heading. */
  neurotoxicityRiskFactorsNote: string;
  /** How often bilirubin is re-measured during admission (§1). Changes no threshold. */
  monitoringFrequency: string[];
  followUpBands: FollowUpBand[];
  followUpBefore12hText: string;
  /** General discharge-follow-up policy shown under the follow-up table. */
  followUpNotes: string[];
  phototherapyStartLabs: string[];
  postPhototherapyNotes: string[];
  escalationSteps: string[];
}

/** Key of the AAP guideline PDF that ships inside the app bundle. */
export const BUILTIN_AAP_GUIDELINE = 'aap2022Guideline';

export interface PhototherapySettings {
  protocol: PhototherapyProtocol;
  aap2022: Aap2022Settings;
  /**
   * Documents offered on the "פרוטוקולים" screen, in order. Managed on the
   * admin page and stored as references — see services/configFilesApi.
   *
   * Deliberately OUTSIDE `aap2022`: these are the department's own documents,
   * not part of the AAP clinical model, so "שחזר ברירות מחדל" on the clinical
   * fields must not throw away files someone uploaded. An empty list is a real
   * answer — it hides the screen — so it is never treated as "unset".
   */
  protocolFiles: ProtocolFile[];
}

/**
 * Starting values for the AAP fields on the admin page — not a configuration.
 * Nothing here reaches a hospital until an admin picks AAP 2022 and saves.
 */
export const DEFAULT_AAP2022_SETTINGS: Aap2022Settings =
  aap2022DefaultsJson as Aap2022Settings;

/**
 * What the "פרוטוקולים" screen offers before anyone uploads anything: the AAP
 * guideline the calculator is built on, and nothing else. The departmental
 * procedure that used to sit next to it was one hospital's document shipped to
 * every hospital — each department now uploads its own on the admin page.
 */
export const DEFAULT_PROTOCOL_FILES: ProtocolFile[] = [
  { title: 'AAP 2022 - Clinical Practice Guideline', builtin: BUILTIN_AAP_GUIDELINE },
];

/**
 * What a hospital with no stored row gets, for one app. Only `protocol` differs
 * between the variants — the AAP form defaults and the shipped document list are
 * the same wherever the AAP calculator is shown.
 */
export const defaultSettingsFor = (variant: PhototherapyVariant): PhototherapySettings => ({
  protocol: variant.defaultProtocol,
  aap2022: DEFAULT_AAP2022_SETTINGS,
  protocolFiles: DEFAULT_PROTOCOL_FILES,
});

const isProtocol = (v: unknown): v is PhototherapyProtocol =>
  v === 'israeliCurves' || v === 'aap2022';

const numberOr = (v: unknown, fallback: number): number =>
  typeof v === 'number' && Number.isFinite(v) ? v : fallback;

const nullableNumber = (v: unknown, fallback: number | null): number | null => {
  if (v === null) return null;
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
};

const stringOr = (v: unknown, fallback: string): string => (typeof v === 'string' ? v : fallback);

const stringArrayOr = (v: unknown, fallback: string[]): string[] =>
  Array.isArray(v) && v.every((s) => typeof s === 'string') ? (v as string[]) : fallback;

const bandsOr = (v: unknown, fallback: FollowUpBand[]): FollowUpBand[] => {
  if (!Array.isArray(v) || v.length === 0) return fallback;
  const bands: FollowUpBand[] = [];
  for (const b of v) {
    if (!b || typeof b !== 'object') return fallback;
    const minDelta = numberOr((b as FollowUpBand).minDelta, NaN);
    const maxDelta = nullableNumber((b as FollowUpBand).maxDelta, NaN);
    const recommendation = (b as FollowUpBand).recommendation;
    if (Number.isNaN(minDelta) || Number.isNaN(maxDelta) || typeof recommendation !== 'string') {
      return fallback;
    }
    bands.push({ minDelta, maxDelta, recommendation });
  }
  return bands;
};

/**
 * The protocol list, or the shipped default when the row predates this field.
 *
 * An **empty array is kept**, unlike every other list here: a hospital that
 * removed the last document meant "show no protocols screen", and falling back
 * to the default would put the AAP guideline back on a screen it had just been
 * taken off. Only a missing or malformed value falls back.
 */
const protocolFilesOr = (v: unknown, fallback: ProtocolFile[]): ProtocolFile[] => {
  if (!Array.isArray(v)) return fallback;
  const files: ProtocolFile[] = [];
  for (const f of v) {
    if (!f || typeof f !== 'object') return fallback;
    const { title, path, builtin } = f as ProtocolFile;
    if (typeof title !== 'string') return fallback;
    // A reference to nothing is not a document; it would render a dead tile.
    if (typeof path !== 'string' && typeof builtin !== 'string') return fallback;
    files.push({
      title,
      ...(typeof path === 'string' ? { path } : {}),
      ...(typeof builtin === 'string' ? { builtin } : {}),
    });
  }
  return files;
};

/**
 * Merges whatever the server stored with the shipped defaults, field by field,
 * so a row saved by an older admin page still yields a complete settings object.
 */
export function normalizePhototherapySettings(
  raw: unknown,
  variant: PhototherapyVariant
): PhototherapySettings {
  const d = defaultSettingsFor(variant);
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const a = (r.aap2022 && typeof r.aap2022 === 'object' ? r.aap2022 : {}) as Record<string, unknown>;
  return {
    protocol: isProtocol(r.protocol) ? r.protocol : d.protocol,
    protocolFiles: protocolFilesOr(r.protocolFiles, d.protocolFiles),
    aap2022: {
      subthresholdDeltaMgDl: numberOr(a.subthresholdDeltaMgDl, d.aap2022.subthresholdDeltaMgDl),
      stopPhototherapyDeltaMgDl: numberOr(a.stopPhototherapyDeltaMgDl, d.aap2022.stopPhototherapyDeltaMgDl),
      escalationDeltaMgDl: numberOr(a.escalationDeltaMgDl, d.aap2022.escalationDeltaMgDl),
      tcbToTsbDeltaMgDl: numberOr(a.tcbToTsbDeltaMgDl, d.aap2022.tcbToTsbDeltaMgDl),
      tcbToTsbAbsoluteMgDl: numberOr(a.tcbToTsbAbsoluteMgDl, d.aap2022.tcbToTsbAbsoluteMgDl),
      bloodTypeDatDeltaMgDl: nullableNumber(a.bloodTypeDatDeltaMgDl, d.aap2022.bloodTypeDatDeltaMgDl),
      hearingTestTsbMgDl: nullableNumber(a.hearingTestTsbMgDl, d.aap2022.hearingTestTsbMgDl),
      prolongedJaundiceFromHours: nullableNumber(a.prolongedJaundiceFromHours, d.aap2022.prolongedJaundiceFromHours),
      prolongedJaundiceDeltaMgDl: numberOr(a.prolongedJaundiceDeltaMgDl, d.aap2022.prolongedJaundiceDeltaMgDl),
      neurotoxicityRiskFactors: stringArrayOr(a.neurotoxicityRiskFactors, d.aap2022.neurotoxicityRiskFactors),
      neurotoxicityRiskFactorsNote: stringOr(
        a.neurotoxicityRiskFactorsNote,
        d.aap2022.neurotoxicityRiskFactorsNote
      ),
      // `jaundiceRiskFactors` / `jaundiceRiskFactorsNote` were dropped. Stored rows
      // may still carry them; they are simply not read, so no migration is needed.
      monitoringFrequency: stringArrayOr(a.monitoringFrequency, d.aap2022.monitoringFrequency),
      followUpBands: bandsOr(a.followUpBands, d.aap2022.followUpBands),
      followUpBefore12hText: stringOr(a.followUpBefore12hText, d.aap2022.followUpBefore12hText),
      followUpNotes: stringArrayOr(a.followUpNotes, d.aap2022.followUpNotes),
      phototherapyStartLabs: stringArrayOr(a.phototherapyStartLabs, d.aap2022.phototherapyStartLabs),
      postPhototherapyNotes: stringArrayOr(a.postPhototherapyNotes, d.aap2022.postPhototherapyNotes),
      escalationSteps: stringArrayOr(a.escalationSteps, d.aap2022.escalationSteps),
    },
  };
}

/**
 * NOTE ON FAILURE BEHAVIOUR — deliberate, do not "improve" it into a fallback.
 * A request that fails must NEVER resolve to the shipped defaults. Doing so
 * would show a hospital configured for AAP 2022 the Israeli curves (or the other
 * way round), i.e. the wrong treatment thresholds, with nothing on screen saying
 * so. Waiting longer, or telling the user to retry, is always the safer answer.
 * Absence of a stored row is different: the server returns `{}` and that legitimately
 * means "not configured" → the default protocol.
 */
export interface FetchSettingsOptions {
  /** Per-attempt ceiling. Generous on purpose — see the note above. */
  timeoutMs?: number;
  /** Total attempts, including the first. */
  attempts?: number;
  /** Base delay between attempts; grows linearly with the attempt number. */
  retryDelayMs?: number;
  /** Stops the retry loop when the caller goes away (unmount, hospital change). */
  signal?: AbortSignal;
}

const DEFAULT_FETCH_OPTIONS: Required<Omit<FetchSettingsOptions, 'signal'>> = {
  timeoutMs: 20000,
  attempts: 3,
  retryDelayMs: 1000,
};

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Resolves with the hospital's settings, or REJECTS. It never resolves with the
 * defaults as a stand-in for an answer it did not get.
 */
export async function fetchPhototherapySettings(
  hospital: string,
  variant: PhototherapyVariant,
  options: FetchSettingsOptions = {}
): Promise<PhototherapySettings> {
  const { timeoutMs, attempts, retryDelayMs } = { ...DEFAULT_FETCH_OPTIONS, ...options };
  const signal = options.signal;
  const url = `/api/configs/${variant.appKey}/${variant.configName}/${encodeURIComponent(hospital)}`;
  let lastError: unknown;

  for (let attempt = 1; attempt <= attempts; attempt++) {
    if (signal?.aborted) throw new Error('phototherapy settings request cancelled');
    try {
      // `handlingErrorLocally` is understood by the axiosConfig wrapper (it silences
      // the global FloatingError toast); `timeout` and `signal` are axios' own.
      const response = await API.get(url, {
        timeout: timeoutMs,
        signal,
        handlingErrorLocally: true,
      } as AxiosRequestConfig);
      return normalizePhototherapySettings(response?.data?.data, variant);
    } catch (err) {
      lastError = err;
      if (signal?.aborted) throw err;
      if (attempt < attempts) await delay(retryDelayMs * attempt);
    }
  }
  throw lastError instanceof Error
    ? lastError
    : new Error(`Could not load phototherapy settings for "${hospital}"`);
}

export interface UsePhototherapySettingsResult {
  /** Null until the real configuration is known. Never a stand-in. */
  settings: PhototherapySettings | null;
  loading: boolean;
  error: Error | null;
  retry: () => void;
}

/**
 * Loads the hospital's settings. While it is working `settings` is null and
 * `loading` is true; if every attempt fails `error` is set and `settings` stays
 * null, so the caller shows a retry prompt rather than a calculator.
 *
 * With no hospital in the route the global `apps` configuration is fetched —
 * that is what the server would fall back to anyway, and it keeps the "always
 * ask, never assume" rule intact.
 */
export function usePhototherapySettings(
  hospital: string | undefined,
  variant: PhototherapyVariant
): UsePhototherapySettingsResult {
  const [settings, setSettings] = useState<PhototherapySettings | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const retry = useCallback(() => setReloadToken((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    fetchPhototherapySettings(hospital || 'apps', variant, { signal: controller.signal })
      .then((s) => {
        if (!cancelled) {
          setSettings(s);
          setLoading(false);
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        console.error('phototherapy settings could not be loaded', err);
        setSettings(null);
        setError(err instanceof Error ? err : new Error('phototherapy settings failed'));
        setLoading(false);
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [hospital, variant, reloadToken]);

  return { settings, loading, error, retry };
}
